// What a conversation is CALLED, who in it is an outsider, and what the line under the name says.
//
// One module, because every fault this surface has produced has been in these four rules and none of
// them was ever run by a test: a raw Teams id standing in for a person's name (B616), the same id
// printed again in the amber band after the name was fixed (B619), "no messages yet" on a conversation
// that ends in a file (B616), and initials invented from the sentence that stands in where there is no
// name at all. They are pure functions of a row, so they are tested here rather than discovered on his
// own 573 conversations, which is where all four were found.
import type { ChatRow } from './api.js';

/**
 * A person Teams never gave a name to: the store holds the id it was known by. Seen live on 2026-09-20
 * as "50b39950-4fd8-4ac4-accf-82e8efaadce8, Alex Roberts +12" at the head of a conversation. An id is
 * not a name, so it is never shown as one; the conversation is named by the people who do have names.
 */
const UNNAMED = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/**
 * The OTHER shape the same thing takes, and the one that was still on the page after the first was
 * fixed: Teams' own identifier for a person it never resolved -- "8:teamsvisitor:17462af3cf3f..." for
 * somebody who joined a meeting by link, "8:orgid:<guid>" for a directory user. Measured on the live
 * page on 2026-09-20: ten distinct ones, heading THIRTEEN conversations, one of them headed by two of
 * them and nobody else. The first filter was written for the bare id and the record called that "the
 * id", so this was read straight past. Nobody's name begins with digits and a colon; of the 274 names
 * on his store, not one is caught by this that a person would recognise.
 */
const MRI = /^\d{1,3}:/;
export const named = (n: string | null | undefined): boolean => {
  const t = (n ?? '').trim();
  return t !== '' && !UNNAMED.test(t) && !MRI.test(t);
};

/**
 * What a row is called: its title, or the people in it who are not you. Your own name is never in it.
 * It also says whether that IS a name. "Just you", "Unnamed chat" and "12 people" are sentences standing
 * in where there is no name, and a sentence has no initials: six conversations on the live page wore the
 * same "JY" badge, which reads as six conversations with one person called J-- Y--.
 */
export function titleOf(c: ChatRow, myName?: string | null): { name: string; real: boolean } {
  if (named(c.title)) return { name: c.title!.trim(), real: true };
  const mine = (myName ?? '').trim().toLowerCase();
  const names = c.people.map((p) => p.name).filter((n) => named(n) && n.trim().toLowerCase() !== mine);
  const unnamed = c.people.filter((p) => !named(p.name)).length;
  if (names.length === 0) {
    return { name: unnamed > 0 ? (unnamed === 1 ? 'Unnamed chat' : `${unnamed} people`) : 'Just you', real: false };
  }
  const more = Math.max(0, names.length - 2) + unnamed;
  if (names.length <= 3 && unnamed === 0) return { name: names.join(', '), real: true };
  return { name: `${names.slice(0, 2).join(', ')} +${more}`, real: true };
}

export const nameOf = (c: ChatRow, myName?: string | null): string => titleOf(c, myName).name;

/** Who in the room is not at this company, in words, for the amber band. */
export function outsiders(people: ChatRow['people']): string {
  // named() here too: the id filter was on the conversation's NAME and not on this line, so a person Teams
  // never named printed as "50b39950-4fd8-4ac4-accf-82e8efaadce8, Chris and 2 more are outside iGame Media"
  // on the live page. A filter that is not applied everywhere the same data appears is not applied.
  const out = people.filter((p) => p.external);
  const namedOut = out.filter((p) => named(p.name)).map((p) => p.name);
  const unnamedOut = out.length - namedOut.length;
  const say = (who: string, plural: boolean) =>
    `${who} ${plural ? 'are' : 'is'} outside iGame Media and can read everything in this conversation.`;
  // When nobody outside has a name the phrase stands for all of them, so there is nothing left to count
  // behind it. Counting them a second time put "2 people and 2 more are outside" on the live page over a
  // conversation with two outsiders in it -- a warning that overstates who can read this is not a warning.
  if (namedOut.length === 0) {
    if (unnamedOut === 0) return say('Somebody here', false);
    return unnamedOut === 1 ? say('Someone', false) : say(`${unnamedOut} people`, true);
  }
  const more = Math.max(0, namedOut.length - 2) + unnamedOut;
  if (more === 0) {
    return namedOut.length === 1 ? say(namedOut[0]!, false) : say(`${namedOut[0]} and ${namedOut[1]}`, true);
  }
  return say(`${namedOut.slice(0, 2).join(', ')} and ${more} more`, true);
}


/**
 * The line under the name. A conversation with messages whose last one carries no text is not empty: it
 * ends with a file, a card or a reaction, and saying "no messages yet" about it (as this page did on
 * 2026-09-20) is a claim about the conversation that is not true.
 */
export function previewOf(c: ChatRow, myName?: string | null): string {
  const mine = (myName ?? '').trim().toLowerCase();
  // named() at the third place the same field is drawn. Two people on the store are known to Teams only
  // by an MRI and neither has sent a message yet, so this has never been on the page -- but a rule applied
  // at some of the places its data appears is not applied, which is how the id reached the amber band
  // after the list had stopped printing it. A sender with no name is simply not named here.
  const who = named(c.lastSender) ? (c.lastSender!.trim().toLowerCase() === mine ? 'You' : c.lastSender) : null;
  if (c.lastPreview) return `${who ? `${who}: ` : ''}${c.lastPreview}`;
  // The record says there is no text, not what the message was: a file, a card and a reaction all look
  // like this, so the line names the set rather than picking one of them.
  if (c.messages > 0) return `${who ? `${who}: ` : ''}attachment or reaction`;
  return c.source === 'cobrain' ? 'No messages yet' : 'Nothing here yet';
}
